import { useEffect, useState } from "react";
import { ProjectDetails } from "../../components/features/project-reports/components/project-utilization-report/project-utilization-report.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Select, Spinner } from "../../components/ui/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useProjectOptions } from "../../hooks/project-reports/use-project-options.ts";
import type { PageProps } from "../../types/page.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { DeleteWorkstreamModal } from "./delete-workstream-modal.tsx";
import type { Workstream, WorkstreamInput } from "./types.ts";
import { useProjectDetails } from "./use-project-details.ts";
import { useWorkstreams } from "./use-workstreams.ts";
import { WorkstreamFormModal } from "./workstream-form-modal.tsx";
import { WorkstreamTable } from "./workstream-table.tsx";
import styles from "./work-stream-page.module.css";

const PAGE_TITLE = "Work Stream";

const WorkStreamPage = ({ title, pageMeta }: PageProps) => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const pageTitle = pageMeta?.title?.trim() || title?.trim() || PAGE_TITLE;
  const [projectId, setProjectId] = useState("");
  const [formMode, setFormMode] = useState<"add" | "edit" | null>(null);
  const [selectedWorkstream, setSelectedWorkstream] = useState<Workstream | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Workstream | null>(null);
  const [showActions, setShowActions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { options: projectOptions, loading: projectsLoading, error: projectsError } = useProjectOptions();
  const { data: projectDetails, loading: detailsLoading, error: detailsError } = useProjectDetails(projectId);
  const {
    workstreams,
    loading: workstreamsLoading,
    error: workstreamsError,
    saveWorkstreams,
    removeWorkstream,
  } = useWorkstreams(projectId);

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  useEffect(() => {
    setFormMode(null);
    setSelectedWorkstream(null);
    setDeleteTarget(null);
    setShowActions(false);
  }, [projectId]);

  const closeForm = () => {
    setFormMode(null);
    setSelectedWorkstream(null);
  };

  const handleSave = async (items: WorkstreamInput[]) => {
    if (!formMode) return;
    setSaving(true);
    try {
      await saveWorkstreams(items, formMode === "edit");
      showSuccess(formMode === "edit" ? "Work stream updated successfully" : "Work streams added successfully");
      closeForm();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to save work streams");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await removeWorkstream(deleteTarget.id);
      showSuccess("Work stream deleted successfully");
      setDeleteTarget(null);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to delete work stream");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageShell>
      <Card padding="3xl" className={styles.filterCard}>
        <div className={styles.projectFilter}>
          <Select
            id="work-stream-project-filter"
            label="Project Name"
            placeholder={projectsLoading ? "Loading..." : "Select Project Name"}
            searchable
            isClearable
            disabled={projectsLoading}
            value={projectId}
            onChange={setProjectId}
            options={projectOptions}
          />
        </div>
        {projectsError ? <p className={styles.error} role="alert">{projectsError}</p> : null}
      </Card>

      {!projectId ? (
        <Card padding="5xl" className={styles.emptyState}>Please select a project to view details and work streams.</Card>
      ) : detailsLoading || workstreamsLoading ? (
        <Card padding="5xl" className={styles.loadingState}>
          <Spinner variant="inline" />
          <span>Loading project details and work streams...</span>
        </Card>
      ) : (
        <div className={styles.content}>
          {detailsError ? (
            <Card padding="5xl" className={`${styles.emptyState} ${styles.error}`} role="alert">{detailsError}</Card>
          ) : projectDetails ? (
            <ProjectDetails data={projectDetails} />
          ) : null}

          <WorkstreamTable
            workstreams={workstreams}
            loading={false}
            error={workstreamsError}
            showActions={showActions}
            onAdd={() => setFormMode("add")}
            onToggleActions={() => setShowActions((visible) => !visible)}
            onEdit={(workstream) => {
              setSelectedWorkstream(workstream);
              setFormMode("edit");
            }}
            onDelete={setDeleteTarget}
          />
        </div>
      )}

      <WorkstreamFormModal
        open={formMode != null}
        mode={formMode ?? "add"}
        initialWorkstream={selectedWorkstream}
        saving={saving}
        onClose={closeForm}
        onSubmit={handleSave}
      />
      <DeleteWorkstreamModal
        workstream={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
};

export default WorkStreamPage;
